<script setup>
import { reactive, ref, watch } from "vue";
import Cart from "../../Shared/Cart";
import ReservationPopup from './ReservationPopup';
import emitter from 'tiny-emitter/instance'
import {Inertia} from "@inertiajs/inertia";
import GuestCheckout from "./Components/GuestCheckout";
import CustomerCheckout from "./Components/CustomerCheckout";
import EmployeeCheckout from "./Components/EmployeeCheckout";
import { Link } from '@inertiajs/inertia-vue3'

const props = defineProps({
  customers: Object,
  addresses: Object,
  defaultAddress: Object,
  auth: Object,
})

const selectedAddress = ref()
const selectedCustomer = ref(null)

const cart = new Cart();
let products = reactive(cart.storage.getCart());

const acceptedTerms = ref(false);
const showReservationPopup = ref(false);

const editableProduct = ref(null);
const currentStep = ref(1);

const showingPanels = reactive([]);

function isEmployee() {
  return (props.auth.user.role === 'organisation administrator') || (props.auth.user.role === 'employee');
}

function showPanel(number){
  showingPanels.includes(number) ?
    showingPanels.splice(showingPanels.indexOf(number), 1) :
    showingPanels.push(number);
}

function removeItemFromCart(productIdx){
  cart.removeItem(productIdx);
  updateCart()
}
function editItemFromCart(productIdx){
  editableProduct.value= cart.getItem(productIdx);

  showReservationPopup.value = true;
}

function updateCart() {
  const cartItems = cart.getItems();
  // Empty cart panel before adding items.
  while(products.length > 0) {
    products.pop();
  }
  cartItems.map(i => {
    products.push(i)
  })
  emitter.emit('updateCart')
}

function showLogin() {
  Inertia.visit(route('login.form', {intended: 'checkout'}))
}

</script>

<template>
  <div class="bg-white">
    <main class="max-w-7xl mx-auto px-4 pt-4 pb-16 sm:px-6 sm:pt-8 sm:pb-24 lg:px-8 xl:px-2 xl:pt-14">
    <h1 class="sr-only">Checkout</h1>

    <div class="max-w-lg mx-auto grid grid-cols-1 gap-x-8 gap-y-16 lg:max-w-none lg:grid-cols-2">
      <div class="max-w-lg mx-auto w-full">
        <h2 class="sr-only">Order summary</h2>

        <div class="flow-root">
          <ul role="list" class="-my-6 divide-y divide-gray-200">
            <li v-for="(product, productIdx) in products" :key="product.id" class="py-6 flex space-x-6">
              <img :src="product.imageSrc" alt="" class="flex-none w-24 h-24 object-center object-cover bg-gray-100 rounded-md" />
              <div class="flex-auto">
                <div class="space-y-1 sm:flex sm:items-start sm:justify-between sm:space-x-6">
                  <div class="flex-auto text-sm font-medium space-y-1">
                    <h3 class="text-gray-900">
                      <a :href="product.href">{{ product.name }}</a>
                    </h3>
                    <p class="text-gray-900">€{{ product.price }} p/d</p>
                    <p class="mt-1 text-sm text-gray-500">Ophaaldatum: {{ new Date(product.pickupDate).toLocaleDateString('nl-NL') }}</p>
                    <p class="mt-1 text-sm text-gray-500">Ophaaltijd: {{ product.pickupTime }}</p>
                    <p class="mt-1 text-sm text-gray-500">Aantal dagen: {{ product.amountOfDays }} </p>
                  </div>

                  <div class="flex-none flex space-x-4">
                    <button v-if="false" @click="editItemFromCart(productIdx)" type="button" class="text-sm font-medium text-primary hover:opacity-80">Bewerken</button>
                    <button @click="removeItemFromCart(productIdx)" type="button" class="text-sm font-medium text-primary hover:opacity-80">Verwijder</button>
                  </div>
                </div>
              </div>
            </li>

            <li v-if="products.length === 0" class="py-12">
              <p class="text-center">
                <svg xmlns="http://www.w3.org/2000/svg" class="inline-block text-gray-300 dark:text-gray-500 h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>

              </p>

              <p class="mt-3 text-center text-gray-500">
                Er zijn geen reserveringen gevonden.
              </p>
            </li>
          </ul>

        </div>

      </div>

      <!-- User is not logged in -->
      <div v-if="!$page.props.auth.customer || !$page.props.auth.customer.email" class="max-w-lg mx-auto w-full">
        <button @click="showLogin" type="button" class="w-full flex items-center justify-center bg-primary border border-primary text-white rounded-md py-2 hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary">
          <span>Login met uw account</span>
        </button>

        <div class="relative mt-8">
          <div class="absolute inset-0 flex items-center" aria-hidden="true">
            <div class="w-full border-t border-gray-200" />
          </div>
          <div class="relative flex justify-center">
            <span class="px-4 bg-white text-sm font-medium text-gray-500"> of </span>
          </div>
        </div>

        <GuestCheckout :reservations="products"/>

      </div>

      <!-- User is logged in -->
      <div v-else class="max-w-lg mx-auto w-full">
        <EmployeeCheckout v-model:selectedCustomer="selectedCustomer"
                          v-if="isEmployee()"
                          :customers="props.customers"
                          :addresses="props.addresses"
                          :reservations="products"
        />

        <CustomerCheckout v-if="!isEmployee()" :reservations="products" :address="defaultAddress"/>
      </div>


    </div>
  </main>
    <ReservationPopup v-model="showReservationPopup" :product="editableProduct"/>
  </div>

  <div class="mt-4">
    <Link
      :href="route('storefront')"
      as="button"
      method="get"
      type="button"
      class="w-full md:w-1/4 flex justify-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-off set-2 focus:ring-primary"
    >
      Verder winkelen
    </Link>
  </div>

</template>

<style>
/* Style */
.v-popper--theme-info .v-popper__inner {
  padding: 15px;
  border: 1px #000000 solid;
  border-radius: 5px;
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.2);
}

.v-popper--theme-info .v-popper__arrow-outer {
  border-color: #000000;
}
</style>
